import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/root";
import { colors, css, fonts, frames, glyphs, useMediaQuery } from "../../../../Styles";
import { PageHeader, ProgressBar, QuickEntryModal, AddAnimalModal, NewInput, Overlay, CheckBox, CheckStyles, TableLayout, FilterDisplayList, FilterPopup } from "../../../../Components";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, SelectFieldOption_Values, TableStyles_Types, NewInputHandles, ValueSteps, ValueType } from "../../../../Definitions";
import { UndefinedOptions, useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../../../Components/_Components";
import { Graph_QL, useAnimal, MaintainAnimal, useFeedCurves } from "../../../../Services";
import { AutoReport } from "../../../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/autoReportCall";
import { RootState, useAppSelector } from "../../../../Store";
import { cloneDeep, keyBy, merge } from "lodash";

import { exportTableToCsv } from "../Reports/_Reports/exportTableToCsv";
import { exportTableToExcel } from "../Reports/_Reports/exportTableToXml";

// Table
import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, noop, Row, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";
import { GenericDataParameters, GenericRow, Units, useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import globalFilter from "../../../../Components/Tables/_Tables/useGlobalFilter";
import { LOCKED_COLUMN_NAMES } from "../../../../Components/Tables/_Tables/getTableContent";
import NewButton, { NewButtonList } from "../../../../Components/BaseComponents/Buttons/newButton";
import FeedLabel from "../../../../Components/ProgressViewer/SubComponents/feedLabel";
import { selectAllFeedCurveMilestoneOptions } from "../../../../Store/OptionsHandlers/optionsCombinedReducer";
import { ProgressTypes } from "../../../../Components/ProgressViewer/_ProgressViewer/progressDefinitions";
import { useDispatch, useSelector } from "react-redux";
import { SET_EDIT_MODE } from "../../../../Store/UI/editModeSlice";
import { useAnimalConsumptionById, useAnimalConsumptionByMultipleIds } from "../../../../Services/Agrisys_GqlDataCaller/ConsumptionCalls/consumptionSlice";
import { useSingleConsumptionByMultipleIds } from "../../../../Components/Animals/_Animals/singleConsumptionByMultiple";
// import { EDIT_MODE, NOT_EDIT_MODE } from "../../../../Store/UI/editModeSlice";
// import { Units } from "../../../../Styles/units";

// const createConditionTableDivStyleFunction = (conditionData: Condition[]) => {
//   const colorTagFromName = (name: string|number) => {return conditionData.find(condition => condition.conditionName === name)?.conditionColorTag}
//   return (name?: string | number):CSSProperties|undefined => { if (!name) {return undefined} return {backgroundColor: colorTagFromName(name), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
// }

function OverFed({header}:{header: string}) {
  return (
    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: "5px", overflow: "hidden" }}>
      <div style={{ width: "130px", flexShrink: "0" }}>{header}</div>
      <FeedLabel cursor="pointer" tooltip/>
    </div>
  )
}

interface AnimalTableData extends GenericRow {
  index: number;
  feedCurveId?: SelectFieldOption_Values;
}
// 
export default function Animals() {
  const dispatch = useDispatch();
  const navigator = useNavigate();
  const { t } = useTranslation();
  let location = useLocation();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  const [quickEntryModal, setQuickEntryModal] = useState(false);
  const [addAnimalModal, setAddAnimalModal] = useState(false);
  const searchRef = useRef<NewInputHandles>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const portalRef = useRef<HTMLDivElement | null>(null);
  
  const mobileXs = useMediaQuery(`(max-width: 650px)`);
  const hideFilterDisplay = useMediaQuery(`(max-width: 765px), (min-width: 1001px) and (max-width: 1100px)`);
  // const divStyleOverride = useMemo(() => (createConditionTableDivStyleFunction(conditionData)),[]); // TODO
  const dirtRef = useRef<MaintainAnimal[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  
  const { feedCurveOptions } = useFeedcurveOptions(UndefinedOptions.undefined);

  const feedCurveMilestones = useAppSelector(selectAllFeedCurveMilestoneOptions).reduce((agg, milestone) => {
    const {feedCurveId} = milestone;
    agg[feedCurveId] = agg[feedCurveId] || []
    agg[feedCurveId].push(milestone) 
    return agg;
  },{} as {[key: number]: (ValueType & { feedCurveId: number; index: number; milestoneId: number;})[]});

  const { locationOptions } = useSectionOptions(UndefinedOptions.undefined);
  const groupOptions = useGroupOptions(UndefinedOptions.undefined);
  const milestoneOptions = useMilestoneOptions();
  const systemOptions = useSystemOptions();
  
  const [autoReport, setAutoReport] = useState<AutoReport>();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getAnimalsInfo, data: animalsState, maintainEntries: updateAnimals } = useAnimal(connection);

  const [animals, setAnimals] = useState(animalsState && animalsState[domain] ? cloneDeep(animalsState[domain].data) : []);
  const consumption = useSingleConsumptionByMultipleIds(animals)
  
  useEffect(() => {
    getAnimalsInfo({ fetchAmount: 51 }, connection);
  }, [connection]);

  useEffect(() => {
    setAnimals(
      animalsState && animalsState[domain]
        ? cloneDeep(animalsState[domain].data)
        : []
    );
  }, [animalsState, domain]);

  useEffect(() => {
    const selected = location.state?.params?.selected;
    if (selected) {
      setAutoReport(selected);
    }
  }, [location.state]);

  useEffect(() => {
    setGlobalFilterValue(searchTerm);
  }, [searchTerm]);

  // #region | Animals Table
  const tableData: AnimalTableData[] = useMemo(() => animals.map((animal, index) => {
    const animalConsumption = consumption(animal.animal_id)

    const progressBarToday = (<ProgressBar barWidth={"130px"} progressType={ProgressTypes.remaining} total={animalConsumption[0].total} progressed={animalConsumption[0].progressed} extension={{ hasExtension: true }} />);
    const animalConsumedToday = <ProgressBar barWidth={"130px"} progressType={ProgressTypes.consumed} total={animalConsumption[0].total} progressed={animalConsumption[0].progressed} extension={{ hasExtension: true }} />
    const progressBarYesterday = (<ProgressBar barWidth={"130px"} progressType={ProgressTypes.remaining} total={animalConsumption[1].total} progressed={animalConsumption[1].progressed} extension={{ hasExtension: true }} />);
    const animalConsumedYesterday = <ProgressBar barWidth={"130px"} progressType={ProgressTypes.consumed} total={animalConsumption[1].total} progressed={animalConsumption[1].progressed} extension={{ hasExtension: true }} />

    const actionButton = (<NewButton
      content={animal.animal_number.toString()}
      buttonType={ButtonTypes.textButton}
      startEnhancer={<glyphs.ArrowSmooth fillColor={colors.greyDarkSecondary} />}
      onClick={() => { RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animal, replace: true, navArgs: { selected: animal.animal_id }, currentDomain: farm }); }}
      overrides={{
        contentStyles: { color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph },
        startEnhancerStyles: { marginRight: "5px" },
      }}
    />);

    return {
      animal_number: editMode ? animal.animal_number : actionButton,
      id: animal.animal_id,
      // animalType: animal.animalType,
      life_number: animal.life_number,
      responderNumber: animal.responder_number,
      // animalCondition: animal.condition? animal.condition : 0,
      system_id: animal.system ? systemOptions?.find(({ label }) => animal.system === label) : undefined,
      animal_group_id: { label: animal.animalGroup.name ?? "", id: animal.animalGroup.groupId },
      location_id: animal.location_id ? locationOptions?.find(({ id }) => animal.location_id === id) : undefined,
      [LOCKED_COLUMN_NAMES.feedCurveId]: { label: animal.feedCurve?.name ?? "", id: animal.feedCurve?.[LOCKED_COLUMN_NAMES.feedCurveId] },
      animalRemainingToday: progressBarToday,
      animalRemainingYesterday: progressBarYesterday,
      animalConsumedToday,
      animalConsumedYesterday,
      [LOCKED_COLUMN_NAMES.milestone_id]: { id: animal.milestone?.[LOCKED_COLUMN_NAMES.milestone_id], label: animal.milestone?.name ?? "" },
      animalMilestoneDay: animal.milestone_day ? Math.floor((new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24)) : t("noData", { defaultValue: "no data" }),
      animalCycleDayTotal: animal.birthday ? Math.floor((new Date().getTime() - animal.birthday) / (1000 * 60 * 60 * 24)) : t("noData", { defaultValue: "no data" }), //animal.cycle,
      index,
    };
  }), [animals, editMode, systemOptions, locationOptions, t, navigator, farm]);

  const accessorData = useMemo<GenericDataParameters<AnimalTableData>[]>(() => [
    { headerValue: t("params.animalId", { defaultValue: "animal no." }), accessor: "animal_number", type: editMode ? ListData_ValueTypes.Number : ListData_ValueTypes.Button, width: "130px", staticColumn: true, filter: true },
    // {headerValue: t("params.animalType", {defaultValue: "animal type"}), accessor: "animalType", type: ListData_ValueTypes.String, sort: true, width: "160px"},
    { headerValue: t("params.lifeNo", { defaultValue: "life no." }), accessor: "life_number", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true, width: "160px" },
    { headerValue: t("params.responderNo", { defaultValue: "responder no." }), accessor: "responderNumber", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true, width: "200px" },
    // {headerValue: t("params.condition", {defaultValue: "condition"}), accessor: "animalCondition", type: ListData_ValueTypes.List, options: conditionOptions, sort: true, width: "160px", divStyleOverride},
    { headerValue: t("params.group", { defaultValue: "group" }), accessor: "animal_group_id", type: ListData_ValueTypes.List, options: groupOptions, sort: true, width: "160px", filter: true },
    { headerValue: t("params.location", { defaultValue: "location" }), accessor: "location_id", type: ListData_ValueTypes.List, options: locationOptions, sort: true, width: "200px" },
    { headerValue: t("feed.feedCurve.feedCurve", { defaultValue: "feed curve", }), accessor: LOCKED_COLUMN_NAMES.feedCurveId, type: ListData_ValueTypes.List, options: feedCurveOptions, sort: true, width: "200px", filter: true },
    { headerValue: (<OverFed header={t("feed.general.remainingToday", { defaultValue: "remaining today" })} />), accessor: "animalRemainingToday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "220px", unit: Units.kg },
    { headerValue: (<OverFed header={t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday" })} />), accessor: "animalRemainingYesterday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "220px", unit: Units.kg },
    { headerValue: (<OverFed header={t("feed.general.consumedToday", { defaultValue: "consumed today" })} />), accessor: "animalConsumedToday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "220px", unit: Units.kg },
    { headerValue: (<OverFed header={t("feed.general.consumedYesterday", { defaultValue: "consumed yesterday" })} />), accessor: "animalConsumedYesterday", type: ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "220px", unit: Units.kg },
    { headerValue: t("milestones.general.latestMilestone", { defaultValue: "curr. milestone", }), accessor: LOCKED_COLUMN_NAMES.milestone_id, type: ListData_ValueTypes.List,
      options: (row?: Row<AnimalTableData>) => {
        if (!row) {
          return milestoneOptions;
        }
        if (row.original[LOCKED_COLUMN_NAMES.feedCurveId]?.id && typeof row.original[LOCKED_COLUMN_NAMES.feedCurveId]?.id === "number") {
          return feedCurveMilestones[Number(row.original[LOCKED_COLUMN_NAMES.feedCurveId]?.id)] ?? []
        }
        return []
      },
      sort: true, width: "200px", filter: true },
    { headerValue: t("milestones.general.milestoneDayNow", { defaultValue: "curr. milestone day", }), accessor: "animalMilestoneDay", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true, width: "230px" },
    { headerValue: t("animalPages.animal.totalCycleDay", { efaultValue: "total cycle day", }), accessor: "animalCycleDayTotal", type: ListData_ValueTypes.Number, valueStep: ValueSteps.integers, sort: true, width: "200px" },
    { headerValue: t("animalPages.animal.systemName", { defaultValue: "system", }), accessor: "system_id", type: ListData_ValueTypes.List, options: systemOptions, sort: true, width: "200px", staticColumn: true, filter: true },
  ], [editMode, feedCurveMilestones, feedCurveOptions, groupOptions, locationOptions, milestoneOptions, systemOptions, t]);

  const columns = useColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: editMode,
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    autoResetPageIndex: false,
    globalFilterFn: globalFilter,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
      columnPinning: {
        left: ["animal_number", "id"],
      },
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    meta: {
      deleteRow: noop,
      // TODO REDUCE BOILER PLATE AND KEEP IT DRY
      updateData: (rowId, columnId, value, row) => {
        if (columnId === "responderNumber") {
          dirtRef.current = Object.values(
            merge(
              {},
              keyBy(dirtRef.current, "animal_id"),
              keyBy(
                [{ animal_id: rowId, responder_number: value ? value + "" : null }],
                "animal_id"
              )
            )
          );
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.responder_number = value ? value + "" : undefined;
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === LOCKED_COLUMN_NAMES.feedCurveId) {
          const selectValue = value as any as SelectFieldOption_Values;
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];

          if (newAnimal.feedCurve) {
            newAnimal.feedCurve[LOCKED_COLUMN_NAMES.feedCurveId] = Number(selectValue.id);
            newAnimal.feedCurve.name = selectValue.label;
          } else {
            newAnimal.feedCurve = {
              name: selectValue.label,
              feedCurveId: Number(selectValue.id),
              feedLimit: 0,
              passFeed: 0,
              stimuli: 0,
            };
          }

          const milestoneInNewFeedCurve = feedCurveMilestones[Number(selectValue.id)] ?? []
          const currentMilestoneInNewFc = milestoneInNewFeedCurve.find((milestone) => milestone.milestoneId === newAnimal.milestone?.milestone_id);
          if (milestoneInNewFeedCurve.length) {
            if (!currentMilestoneInNewFc) {
              newAnimal.milestone = {
                name: milestoneInNewFeedCurve[0].label,
               milestone_id: milestoneInNewFeedCurve[0].milestoneId,
              };
              newAnimal.milestone_day = Date.now();
            }
          } else {
            newAnimal.milestone = undefined;
          }
          let milestoneChange: {fk_feedcurve_milestones?: number | null} = {};

          if (milestoneInNewFeedCurve?.length && !currentMilestoneInNewFc) {
            milestoneChange = {fk_feedcurve_milestones: milestoneInNewFeedCurve[0].id}
          }

         if (!currentMilestoneInNewFc && !milestoneInNewFeedCurve?.length) {
          milestoneChange = {fk_feedcurve_milestones: null}
         }

          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{
              animal_id: rowId,
              feedcurve_id: selectValue.id,
              ...milestoneChange  
            }], "animal_id")
          ));
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalCycleDayTotal") {
          const newValue = Date.now() - Number(value) * 86400000
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, birthday: Math.round(newValue / 1000) }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.birthday = newValue;
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "life_number") {
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, [columnId]: Number(value) }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.life_number = Number(value);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animal_group_id") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, animal_group_id: selectValue.id }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          if (newAnimal.animalGroup) {
            newAnimal.animalGroup.groupId = Number(selectValue.id);
            newAnimal.animalGroup.name = selectValue.label;
          } else {
            newAnimal.animalGroup = {
              name: selectValue.label,
              groupId: Number(selectValue.id),
            };
          }
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "location_id") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, location: selectValue.id }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.location_id = Number(selectValue.id);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalMilestoneDay") {
          const newValue = Date.now() - Number(value) * 86400000;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animal_id"),
            keyBy([{ animal_id: rowId, milestone_day: Math.round(newValue / 1000), }], "animal_id")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.milestone_day = newValue;
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === LOCKED_COLUMN_NAMES.milestone_id) {
          const selectValue = value as any as SelectFieldOption_Values;
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex]; // selected value might not be correct
          if (row.original.feedCurveId?.id && typeof row.original.feedCurveId?.id === "number") {
            const milestone = feedCurveMilestones[Number(row.original.feedCurveId?.id)].find(({id}) => id === Number(selectValue.id))
            if (milestone) {
              newAnimal.milestone = {
                milestone_id: milestone.milestoneId,
                name: milestone.label,
              };
              newAnimal.milestone_day = Date.now();
              dirtRef.current = Object.values(merge({},
                keyBy(dirtRef.current, "animal_id"),
                keyBy([{ animal_id: rowId, fk_feedcurve_milestones: milestone.id }], "animal_id")
              ));
            }
          }
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
      },
    },
  });

  const reactiveTable = ReactiveTable({
    minRows: 30,
    table,
    tableType: TableStyles_Types.primaryTable,
    emptyText: t("animalPages.animals.emptyText", {
      defaultValue: "No Animal Found",
    }),
    // renderFooter: false,
    tableToggled: editMode,
  });
  // #endregion

  const filteredData = table.getFilteredRowModel().rows.map(row => row.original);
  const fileLabel = t("animalPages.animals.animals", { defaultValue: "animals" });

  // #region | Button List
  const useContents = (): [string[], JSX.Element[], (() => void)[]] => {
    const buttonEntries: { label: string; glyphs: JSX.Element; onClick: () => void; }[] = [];

    if (editMode) {
      buttonEntries.push(
        { label: t("buttons.save", { defaultValue: "save" }), glyphs: glyphs.Check({}), onClick: () => {
          if (updateAnimals) {
            updateAnimals({ data: dirtRef.current })
          };
          dirtRef.current = []; dispatch(SET_EDIT_MODE(false))
        }},
        { label: t("buttons.cancel", { defaultValue: "cancel" }), glyphs: glyphs.Close({}), onClick: () => {
          setAnimals( animalsState && animalsState[domain]
            ? cloneDeep(animalsState[domain].data)
            : []
          );
          dirtRef.current = [];
          dispatch(SET_EDIT_MODE(false))
        }}
      )
    }

    buttonEntries.push(
      { label: t("buttons.add", { defaultValue: "add" }), glyphs: glyphs.Add({}), onClick: () => setAddAnimalModal(true) },
      { label: t("buttons.edit", { defaultValue: "edit" }), glyphs: glyphs.Edit({}), onClick: () => dispatch(SET_EDIT_MODE(true)) }
    )

    if (!mobileXs) {
      buttonEntries.push(
        { label: t("buttons.excel", { defaultValue: "excel" }), glyphs: glyphs.Excel({}), onClick: () => exportTableToExcel(filteredData, `${fileLabel}.xlsx`, fileLabel, accessorData) },
        { label: t("buttons.csv", { defaultValue: "csv" }), glyphs: glyphs.Csv({}), onClick: () => exportTableToCsv(filteredData, `${fileLabel}.csv`, accessorData) }
      )
    }

    const labels = buttonEntries.map((b) => b.label);
    const icons = buttonEntries.map((b) => b.glyphs)
    const onclicks = buttonEntries.map((b) => b.onClick);
    return [labels, icons, onclicks];
  };

  const ButtonRow = memo(({ editTable }: { editTable: boolean }) => {
    const [contentsRes, icons, onclicks] = useContents();

    return (
      <NewButtonList
        contents={contentsRes}
        onClick={(i) => { onclicks[i]?.() }}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={icons}
        disabled={editTable ? [false, false, true, true, true, true] : [false, false, false]}
        overrides={(index) => ({
          baseStyles: { backgroundColor: editTable && index === 3 ? colors.greyPrimary : colors.greySecondary },
          disabledStyles: { filter: editTable && (index === 2 || index > 3) ? "brightness(0.5)" : undefined },
        })}
      />
    );
  });
  // #endregion
   
  return (
    <frames.Page_inner>
      {editMode ? <Overlay beforeUnload onClickAlert /> : <></>}
      
      <PageHeader
        zIndex={99}
        topHeader={t(`menuHeaders.${Pages.animals}`, { defaultValue: "animals" })}
        subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
        optionButtons={{
          contents: [ t("animalPages.animals.quickEntry", { defaultValue: "Quick Entry" }) ],
          onClick: () => setQuickEntryModal(true)
        }}
      />
      <frames._page_inner_content>
          <TableLayout
            hideOnMobile
            zIndex={editMode ? 999 : 0}
            searchbBarArea={
              <NewInput ref={searchRef}
                startEnhancer={glyphs.Search({})}
                onChange={(value: string) => {setSearchTerm(value)}}
                fieldOverrides={{fieldStyle: NewFieldStyles.greySecondary}}
                placeholder={t("search", { defaultValue: "search" })}
                isDisabled={editMode}
              />
            }
            filterDisplayListArea={
              <FilterDisplayList filters={columnFilters} disabled={editMode} setColumnFilters={setColumnFilters} />
            }
            buttonArea={ <>
              <FilterPopup<AnimalTableData> disabled={editMode} accessorData={accessorData} table={table} autoReport={autoReport ? { ...autoReport, auto_report_id: autoReport.auto_report_id ?? "", filter: autoReport.filter ?? [] } : undefined} />
              <ButtonRow editTable={editMode}/>
            </> }
            editableTable={editMode}
            tableArea={animals.length === 0 ? <p style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>{t("animalPages.animals.emptyText", { defaultValue: "No animals found" })}</p> : <>{reactiveTable}</>}
            paginationBtns={{
              onPrev: () => { if (table.getCanPreviousPage()) { getAnimalsInfo({ fetchAmount: 51, offset: 51 * (table.getState().pagination.pageIndex - 1) }, connection); table.previousPage() } },
              onNext: () => { if (table.getCanNextPage()) { getAnimalsInfo({ fetchAmount: 51, offset: 51 * (table.getState().pagination.pageIndex + 1) }, connection); table.nextPage() } },
              canNextPage: !table.getCanNextPage(),
              canPreviousPage: !table.getCanPreviousPage(),
              thisPageIndex: table.getState().pagination.pageIndex + 1,
              pageCount: table.getPageCount()
            }}
            additionalArea={editMode ? <div style={{display: "flex", flexDirection: "column", gap: "5px"}}>
              <div style={{ display: "grid", gridTemplateColumns: "200px 60px", gridTemplateRows: "auto", gap: "20px" }}>
                <p style={{ ...fonts.fontSize4, ...fonts.paragraph, ...fonts.fontWeight2, color: colors.whitePrimary, borderBottom: "2px solid "+colors.greySecondary+80 }}>{t("animalPages.animals.columns", { defaultValue: "Columns" })}</p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {table.getAllColumns().map((col) => (
                  <div style={{display: "grid", gridTemplateColumns: "200px 60px", gridTemplateRows: "auto", gap: "20px"}}>
                    <CheckBox
                      key={col.id}
                      text={t(`params.${col.id}`)}
                      disabled={!col.getCanHide()}
                      isChecked={col.getIsVisible()}
                      onChange={(checked: boolean) => col.toggleVisibility(checked)}
                      checkStyle={CheckStyles.greyPrimary}
                    />
                    {/* <NewInput
                      fieldOverrides={{
                        fieldType: NewFieldTypes.tertiaryField,
                        fieldStyle: NewFieldStyles.greySecondary,
                        fieldWidth: "60px"
                      }}
                      isDisabled={!col.getIsVisible()}
                      valueOverrides={{
                        valueType: "number"
                      }}
                    /> */}
                  </div>
                ))}</div>
            </div> : undefined}
          />
      </frames._page_inner_content>

      <AddAnimalModal
        isModalOpen={addAnimalModal}
        closeModal={() => setAddAnimalModal(false)}
        connect={{
          connection: connection,
          domain: domain
        }}
      />
      <QuickEntryModal
        isModalOpen={quickEntryModal}
        closeModal={() => setQuickEntryModal(false)}
      />

    </frames.Page_inner>
  );
}
